import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  cancelSendJob,
  getSendJobById,
  getSendJobProgress,
  getSendJobs,
  pauseSendJob,
  resumeSendJob,
} from '../services/send-jobs.service';
import type {
  CancelSendJobResult,
  GetSendJobsParams,
  SendJob,
  SendJobProgress,
  SendJobsListResult,
} from '../types/send-jobs.types';

const activeStatuses = new Set(['queued', 'sending', 'paused']);

const invalidateSendJob = (
  queryClient: ReturnType<typeof useQueryClient>,
  itemId?: string
) => {
  queryClient.invalidateQueries({ queryKey: ['send-jobs'] });
  if (itemId) {
    queryClient.invalidateQueries({ queryKey: ['send-jobs', 'detail', itemId] });
    queryClient.invalidateQueries({ queryKey: ['send-jobs', 'progress', itemId] });
  }
};

export const isActiveSendJobStatus = (status: string | null | undefined) =>
  activeStatuses.has(status?.toLowerCase() ?? '');

export const useGetSendJobs = (
  params: GetSendJobsParams = {},
  options: { enabled?: boolean } = {}
) =>
  useGlobalQuery<SendJobsListResult>({
    queryKey: ['send-jobs', 'list', params],
    queryFn: () => getSendJobs(params),
    staleTime: 15 * 1000,
    enabled: options.enabled ?? true,
  });

export const useGetSendJobById = (
  itemId: string,
  options: { enabled?: boolean } = {}
) => {
  const id = itemId.trim();
  return useGlobalQuery<SendJob>({
    queryKey: ['send-jobs', 'detail', id],
    queryFn: () => getSendJobById(id),
    staleTime: 15 * 1000,
    enabled: (options.enabled ?? true) && Boolean(id),
  });
};

export const useGetSendJobProgress = (
  itemId: string,
  options: { enabled?: boolean; status?: string | null } = {}
) => {
  const id = itemId.trim();
  return useGlobalQuery<SendJobProgress>({
    queryKey: ['send-jobs', 'progress', id],
    queryFn: () => getSendJobProgress(id),
    staleTime: 5 * 1000,
    refetchInterval: isActiveSendJobStatus(options.status) ? 5000 : false,
    enabled: (options.enabled ?? true) && Boolean(id),
  });
};

export const usePauseSendJob = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<SendJob, Error, string>({
    mutationFn: pauseSendJob,
    onSuccess: (_result, itemId) => invalidateSendJob(queryClient, itemId),
  });
};

export const useResumeSendJob = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<SendJob, Error, string>({
    mutationFn: resumeSendJob,
    onSuccess: (_result, itemId) => invalidateSendJob(queryClient, itemId),
  });
};

export const useCancelSendJob = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<CancelSendJobResult, Error, string>({
    mutationFn: cancelSendJob,
    onSuccess: (_result, itemId) => invalidateSendJob(queryClient, itemId),
  });
};
